import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2, Save, Loader2, Search, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react'
import { api } from '../../../lib/api'

function RawEvents({ lineId, line, onCommitSuccess }) {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // Extraer Factory y ProdLine del objeto line
  const getFactoryAndProdLine = () => {
    if (!line) return { factory: '', prodLine: '' }
    
    let factory = line.factory || line.Factory || ''
    
    if (!factory && line.hierarchicalLevel) {
      if (Array.isArray(line.hierarchicalLevel) && line.hierarchicalLevel.length > 0) {
        factory = line.hierarchicalLevel[0]
      } else if (typeof line.hierarchicalLevel === 'string') {
        const parts = line.hierarchicalLevel.split('/')
        if (parts.length > 0) {
          factory = parts[0].trim()
        }
      }
    }
    
    const prodLine = line.code || line.Code || line.name || line.Name || ''
    
    return { factory, prodLine }
  }

  const { factory, prodLine } = getFactoryAndProdLine()

  // React Query: Obtener eventos raw
  const {
    data: events = [],
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['raw-events', factory, prodLine],
    queryFn: async () => {
      if (!factory || !prodLine) {
        return []
      }

      try {
        const response = await api.get('/api/events/raw', {
          headers: {
            'Factory': factory,
            'ProdLine': prodLine
          }
        })
        
        return Array.isArray(response) ? response : (response?.data || response?.events || [])
      } catch (err) {
        // Si el error es 403 o "Registro no existe", retornar array vacío (no es un error crítico)
        if (err.isExpected || (err.message && (err.message.includes('Registro no existe') || err.status === 403))) {
          return []
        }
        throw err
      }
    },
    enabled: !!factory && !!prodLine && !!lineId,
    staleTime: 30000, // 30 segundos
    refetchInterval: 60000, // Refrescar cada minuto
  })

  // React Query Mutation: Eliminar evento
  const deleteMutation = useMutation({
    mutationFn: async (eventId) => {
      await api.get('/api/events/raw/del', {
        headers: {
          'ID': String(eventId)
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-events', factory, prodLine] })
    },
    onError: (err) => {
      console.error('Error deleting raw event:', err)
    }
  })

  // React Query Mutation: Commit evento
  const commitMutation = useMutation({
    mutationFn: async (event) => {
      const eventId = event.ID || event.id
      const eventTime = event.EventTime || event.eventTime || event.timestamp || event.Timestamp || new Date().toISOString()
      const system = event.System || event.system || ''
      const machine = event.Machine || event.machine || ''
      const part = event.Part || event.part || ''
      const eventType = event.Event || event.event || ''

      await api.get('/api/events/raw/tocommit', {
        headers: {
          'ID': String(eventId),
          'EventTime': eventTime,
          'Factory': factory,
          'ProdLine': prodLine,
          'System': system,
          'Machine': machine,
          'Part': part,
          'X-Event-Type': String(eventType)
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-events', factory, prodLine] })
      // Invalidar también la query de commit events para que se refresque automáticamente
      queryClient.invalidateQueries({ queryKey: ['commit-events', factory, prodLine] })
      // Cambiar automáticamente a la pestaña de Commit Events después de un commit exitoso
      if (onCommitSuccess) {
        console.log('✅ [RawEvents] Commit exitoso, cambiando a pestaña Commit Events')
        onCommitSuccess()
      }
    },
    onError: (err) => {
      console.error('Error committing event:', err)
    }
  })

  const handleDelete = async (eventId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      return
    }
    deleteMutation.mutate(eventId)
  }

  const handleCommit = async (event) => {
    if (!window.confirm('¿Declarar este evento en Commit? Se moverá a Commit Events y se abrirá esa pestaña.')) {
      return
    }
    commitMutation.mutate(event)
  }

  // Ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        }
      }
      return { key, direction: 'asc' }
    })
  }

  // Filtrar y ordenar eventos
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events

    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = events.filter(event => {
        const id = String(event.ID || event.id || '')
        const eventNum = String(event.Event || event.event || '')
        const factory = String(event.Factory || event.factory || '').toLowerCase()
        const prodLine = String(event.ProdLine || event.prodLine || '').toLowerCase()
        const system = String(event.System || event.system || '').toLowerCase()
        const machine = String(event.Machine || event.machine || '').toLowerCase()
        const part = String(event.Part || event.part || '').toLowerCase()
        
        return id.includes(query) ||
               eventNum.includes(query) ||
               factory.includes(query) ||
               prodLine.includes(query) ||
               system.includes(query) ||
               machine.includes(query) ||
               part.includes(query)
      })
    }

    // Ordenar por fecha descendente por defecto (más reciente primero)
    // Si hay un sortConfig específico, usar ese, sino ordenar por EventTime DESC
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key] || ''
        let bVal = b[sortConfig.key] || ''

        // Manejar diferentes formatos de campos
        if (sortConfig.key === 'ID') {
          aVal = a.ID || a.id || 0
          bVal = b.ID || b.id || 0
        } else if (sortConfig.key === 'Event') {
          aVal = a.Event || a.event || 0
          bVal = b.Event || b.event || 0
        } else if (sortConfig.key === 'EventTime') {
          aVal = new Date(a.EventTime || a.eventTime || a.timestamp || 0).getTime()
          bVal = new Date(b.EventTime || b.eventTime || b.timestamp || 0).getTime()
        } else {
          aVal = String(aVal).toLowerCase()
          bVal = String(bVal).toLowerCase()
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    } else {
      // Ordenar por fecha descendente por defecto (más reciente primero)
      filtered = [...filtered].sort((a, b) => {
        const aTime = new Date(a.EventTime || a.eventTime || a.timestamp || 0).getTime()
        const bTime = new Date(b.EventTime || b.eventTime || b.timestamp || 0).getTime()
        return bTime - aTime // Descendente (más reciente primero)
      })
    }

    return filtered
  }, [events, searchQuery, sortConfig])

  // Paginación
  const totalPages = Math.ceil(filteredAndSortedEvents.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedEvents = filteredAndSortedEvents.slice(startIndex, endIndex)

  // Resetear página cuando cambie la búsqueda, filtros o línea
  useEffect(() => {
    setCurrentPage(1)
  }, [lineId, searchQuery])

  // Ajustar página si está fuera de rango
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
      // Scroll al inicio de la tabla
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Renderizar icono de ordenamiento
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="text-gray-400" />
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="text-blue-400" />
      : <ArrowDown size={14} className="text-blue-400" />
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    )
  }

  if (isError && error && !error.message?.includes('Registro no existe')) {
    return (
      <div className="text-red-500 p-4 bg-red-900/20 rounded-lg">
        <p className="font-semibold">Error al cargar eventos</p>
        <p className="text-sm">{error.message || 'Error desconocido'}</p>
        <button
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          Reintentar
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Raw Events</h3>
          <p className="text-sm text-gray-400">
            {filteredAndSortedEvents.length} {filteredAndSortedEvents.length === 1 ? 'evento' : 'eventos'}
            {searchQuery && ` (filtrados de ${events.length})`}
            {totalPages > 1 && ` - Página ${currentPage} de ${totalPages}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
            title="Refrescar"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por ID, Evento, Factory, Línea, Sistema, Máquina, Parte..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Tabla de eventos */}
      {filteredAndSortedEvents.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-2">
            {searchQuery ? 'No se encontraron eventos que coincidan con la búsqueda' : 'No hay eventos raw para esta línea'}
          </p>
          <p className="text-sm text-gray-500">
            {searchQuery ? 'Intenta con otros términos de búsqueda' : 'Los eventos raw se crean automáticamente desde el sistema'}
          </p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('ID')}
                  >
                    <div className="flex items-center gap-2">
                      ID
                      <SortIcon columnKey="ID" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('EventTime')}
                  >
                    <div className="flex items-center gap-2">
                      Fecha/Hora
                      <SortIcon columnKey="EventTime" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('Event')}
                  >
                    <div className="flex items-center gap-2">
                      Evento
                      <SortIcon columnKey="Event" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('Factory')}
                  >
                    <div className="flex items-center gap-2">
                      Factory
                      <SortIcon columnKey="Factory" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('ProdLine')}
                  >
                    <div className="flex items-center gap-2">
                      Línea
                      <SortIcon columnKey="ProdLine" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Sistema
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Máquina
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Parte
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paginatedEvents.map((event) => {
                  const eventId = event.ID || event.id
                  return (
                    <tr
                      key={eventId}
                      className="hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-white">{eventId}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">
                          {event.EventTime || event.eventTime
                            ? new Date(event.EventTime || event.eventTime).toLocaleString('es-ES', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-semibold text-blue-400">
                          {event.Event || event.event || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.Factory || event.factory || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.ProdLine || event.prodLine || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.System || event.system || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.Machine || event.machine || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.Part || event.part || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleCommit(event)}
                            disabled={commitMutation.isPending}
                            className="p-2 text-green-400 hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
                            title="Guardar / Declarar en Commit"
                          >
                            {commitMutation.isPending ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Save size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(eventId)}
                            disabled={deleteMutation.isPending}
                            className="p-2 text-red-400 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                            title="Eliminar"
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          
          {/* Controles de paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900/50 border-t border-gray-700">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-300">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
              <div className="text-sm text-gray-400">
                Mostrando {startIndex + 1}-{Math.min(endIndex, filteredAndSortedEvents.length)} de {filteredAndSortedEvents.length}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default RawEvents

